import { apiClient } from "./client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface KretsMember {
    id: string;
    name: string;
    teamId: string;
    teamName: string;
    homeLatitude?: number;
    homeLongitude?: number;
    homeAddress?: string;
    phoneNumber?: string;
    userId?: string;
    userName?: string;
    createdAt: string;
    updatedAt?: string;
}

export interface CreateKretsMemberRequest {
    name: string;
    teamId: string;
    homeLatitude?: number;
    homeLongitude?: number;
    homeAddress?: string;
    phoneNumber?: string;
    userId?: string;
}

export interface UpdateKretsMemberRequest {
    name?: string;
    homeLatitude?: number;
    homeLongitude?: number;
    clearHomeLocation?: boolean;
    homeAddress?: string;
    /** Tom sträng nollställer numret, undefined lämnar det orört. */
    phoneNumber?: string;
    userId?: string;
    clearUserId?: boolean;
}

export interface KretsMemberPin {
    type: "Feature";
    id: string;
    geometry: { type: "Point"; coordinates: [number, number] };
    properties: {
        id: string;
        name: string;
        teamId: string;
        teamName: string;
        homeAddress?: string;
        /**
         * Ett av utdelarens uppdrag (planerat/pågående) — godtyckligt valt när hen har flera.
         * Använd det inte som etikett på hemadress-nålen; där hör personens namn hemma.
         */
        assignmentName?: string | null;
    };
}

export interface KretsMemberLocationsResponse {
    type: "FeatureCollection";
    features: KretsMemberPin[];
}

export const fetchKretsMembers = async (params?: { teamId?: string; search?: string }): Promise<KretsMember[]> => {
    const response = await apiClient.get<KretsMember[]>("/krets-members", { params });
    return response.data;
};

export const createKretsMember = async (request: CreateKretsMemberRequest): Promise<KretsMember> => {
    const response = await apiClient.post<KretsMember>("/krets-members", request);
    return response.data;
};

export const updateKretsMember = async (id: string, request: UpdateKretsMemberRequest): Promise<KretsMember> => {
    const response = await apiClient.put<KretsMember>(`/krets-members/${id}`, request);
    return response.data;
};

export const deleteKretsMember = async (id: string): Promise<void> => {
    await apiClient.delete(`/krets-members/${id}`);
};

export const fetchKretsMemberLocations = async (municipalityPolygonId?: string): Promise<KretsMemberLocationsResponse> => {
    const params = municipalityPolygonId ? { municipalityPolygonId } : undefined;
    const response = await apiClient.get<KretsMemberLocationsResponse>("/krets-members/locations", { params });
    return response.data;
};

export const useKretsMembers = (params?: { teamId?: string; search?: string }) => {
    return useQuery({
        queryKey: ["krets-members", params],
        queryFn: () => fetchKretsMembers(params),
        staleTime: 2 * 60 * 1000,
    });
};

export const useKretsMemberLocations = (enabled: boolean, municipalityPolygonId?: string | null) => {
    return useQuery({
        queryKey: ["krets-members", "locations", municipalityPolygonId ?? "all"],
        queryFn: () => fetchKretsMemberLocations(municipalityPolygonId ?? undefined),
        enabled,
        staleTime: 2 * 60 * 1000,
    });
};

export const useCreateKretsMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createKretsMember,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["krets-members"] });
        },
    });
};

export const useUpdateKretsMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...request }: { id: string } & UpdateKretsMemberRequest) =>
            updateKretsMember(id, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["krets-members"] });
        },
    });
};

export const useDeleteKretsMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteKretsMember,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["krets-members"] });
        },
    });
};
